// Fetch wrapper for the external Benji widget. Reads the magic-link preview
// cookie and forwards it as the X-CCB-Benji-Preview header so the dashboard
// backend's preview-token gate can validate the request.

import { readPreviewCookie } from "./session-store";

declare global {
  interface Window {
    __BENJI__?: {
      apiBase?: string;
      ctaVariant?: "A" | "B" | "C" | "D" | "E" | "F";
      /** Optional WP/QA-side override for the launcher microinteraction
       * variant. "auto" or unset → hash-assigned per session. */
      interactionVariant?: "A" | "B" | "C" | "auto";
      publicMode?: boolean;
    };
  }
}

function apiBase(): string {
  // ?? only fires on null/undefined; treat empty string as missing too so
  // an empty WP option doesn't force the widget to fetch a relative URL
  // (which 404s on coffeecartboys.com because the route lives on hq.…).
  const fromConfig = (window.__BENJI__?.apiBase ?? "").trim();
  return fromConfig || "https://hq.coffeecartboys.com";
}

function previewHeader(): Record<string, string> {
  const cookie = readPreviewCookie();
  return cookie ? { "x-ccb-benji-preview": cookie } : {};
}

export interface SendInput {
  sessionId: string;
  threadId: string | null;
  message: string;
}

export interface StreamEvent {
  type: string;
  [key: string]: unknown;
}

export async function streamChat(
  input: SendInput,
  onEvent: (evt: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${apiBase()}/api/chat/external`, {
    method: "POST",
    signal,
    headers: { "content-type": "application/json", ...previewHeader() },
    body: JSON.stringify({
      sessionId: input.sessionId,
      threadId: input.threadId ?? undefined,
      message: input.message,
    }),
  });
  if (!res.ok || !res.body) {
    const fallback = await res.json().catch(() => ({}));
    const msg = (fallback as { message?: string }).message ?? `Request failed (${res.status})`;
    onEvent({ type: "error", message: msg });
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (!line) continue;
      try {
        const evt = JSON.parse(line) as StreamEvent;
        onEvent(evt);
      } catch {
        /* ignore */
      }
    }
  }
}

export interface CaptureInput {
  sessionId: string;
  threadId: string | null;
  name: string;
  email: string;
  phone?: string;
  contactPref: "Text" | "Email" | "Call";
  eventType?: string;
  guests?: number;
  eventDate?: string;
  locationCity?: string;
  /** 5-digit US zip if known — server resolves to city/state. */
  locationZip?: string;
  /** Free-text address — server extracts any 5-digit zip from it. */
  locationAddress?: string;
  serviceWindow?: string;
  notes?: string;
}

export interface CaptureResult {
  ok: boolean;
  leadId?: string;
  isReturning?: boolean;
  message?: string;
}

export interface FeedbackResult {
  ok: boolean;
  value?: "up" | "down" | null;
  message?: string;
}

/**
 * Rate an assistant message. Toggles — calling with the same rating clears.
 * Returns the new value the server stored (so the UI matches the server).
 */
export async function submitFeedback(
  messageId: string,
  rating: "up" | "down",
  sessionId: string,
): Promise<FeedbackResult> {
  try {
    const res = await fetch(`${apiBase()}/api/chat/external/messages/${messageId}/feedback`, {
      method: "PATCH",
      headers: { "content-type": "application/json", ...previewHeader() },
      body: JSON.stringify({ rating, sessionId }),
    });
    const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      return { ok: false, message: (json.message as string) ?? `Feedback failed (${res.status})` };
    }
    return { ok: true, value: (json.value as "up" | "down" | null) ?? null };
  } catch (err) {
    return { ok: false, message: (err as Error)?.message ?? "Network error" };
  }
}

/**
 * Fire-and-forget funnel-tracking call. Never throws — analytics failures
 * shouldn't break the chat UX. Server-side is idempotent per (sessionId,
 * event) so accidental double-fires are safe.
 */
export async function trackEvent(
  sessionId: string,
  variant: string,
  interactionVariant: string,
  event: "shown" | "opened" | "messaged" | "captured",
): Promise<void> {
  // Suppress A/B funnel tracking for team previews — Sean / Julian
  // role-playing as prospects would otherwise pollute every variant's
  // shown/opened/messaged counts. Real visitors don't have the cookie.
  if (readPreviewCookie()) return;
  try {
    await fetch(`${apiBase()}/api/chat/external/track`, {
      method: "POST",
      headers: { "content-type": "application/json", ...previewHeader() },
      body: JSON.stringify({ sessionId, variant, interactionVariant, event }),
      keepalive: true, // survives the page unloading mid-request
    });
  } catch {
    /* swallow */
  }
}

export async function submitCapture(input: CaptureInput): Promise<CaptureResult> {
  try {
    const res = await fetch(`${apiBase()}/api/chat/external/capture`, {
      method: "POST",
      headers: { "content-type": "application/json", ...previewHeader() },
      body: JSON.stringify(input),
    });
    const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      return { ok: false, message: (json.message as string) ?? `Submit failed (${res.status})` };
    }
    return {
      ok: true,
      leadId: json.leadId as string | undefined,
      isReturning: Boolean(json.isReturning),
    };
  } catch (err) {
    return { ok: false, message: (err as Error)?.message ?? "Network error" };
  }
}
